import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowRight, Building2, Heart, LayoutGrid, List, MapPin, MessageSquare, Pencil, Rocket, Send, ShieldCheck, Sparkles, Undo2, Users } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import Loader from "@/components/ui/loader";
import { PageHero } from "@/components/interior-page";
import { Pagination, usePageSize } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import { orgScopedPath } from "@/lib/orgDoor";
import BuyerChats from "@/components/BuyerChats";
import { ChatSideDrawer } from "@/components/ChatSideDrawer";

// Multiples of 12 fill every row of the 2/3/4-up card grid.
const PAGE_SIZE_OPTIONS = [12, 24, 48];
const DEFAULT_PAGE_SIZE = 24;

interface LookupItem { id: number; name: string }

interface MyInterest {
  id: string;
  listing_id: string;
  message: string | null;
  created_at: string;
  listing_title: string | null;
  listing_reference_code: string | null;
  listing_category_id: number | null;
  asking_price: number | null;
  revenue: number | null;
  ebitda: number | null;
  country_id: number | null;
  state_id: number | null;
  city_id: number | null;
  city_name: string | null;
  state_name: string | null;
  country_name: string | null;
  agent_name: string | null;
  agent_avatar_url: string | null;
  listing_image_url: string | null;
  display_overview: string | null;
  // Where the deal stands now (listing_statuses id / label).
  listing_status_id: number | null;
  listing_status_label: string | null;
  listing_removed: boolean;
  unread_count: number;
}

const ACTIVE_STATUS_ID = 3;

/** A chip for a listing that has left the market, or null while it's live. */
function dealStatusChip(item: MyInterest): { label: string; className: string } | null {
  if (item.listing_removed) {
    return { label: "No longer available", className: "border-rose-500/30 bg-rose-500/10 text-rose-500" };
  }
  if (!item.listing_status_id || item.listing_status_id === ACTIVE_STATUS_ID) return null;
  // Under offer is still in play; everything else has closed.
  const inPlay = item.listing_status_id === 4;
  return {
    label: item.listing_status_label ?? "Unavailable",
    className: inPlay
      ? "border-amber-500/30 bg-amber-500/10 text-amber-500"
      : "border-rose-500/30 bg-rose-500/10 text-rose-500",
  };
}

const fmtCompact = (v: number | null): string => {
  if (!v) return "—";
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(v % 1_000_000_000 === 0 ? 0 : 1)}b`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}m`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return `$${v}`;
};

interface ComingSoonInterest {
  interest_id: number;
  pipeline_id: string;
  reference_code: string | null;
  title: string;
  industry_category: string | null;
  potential_category: string | null;
  location: string | null;
  image_url: string | null;
  business_description: string | null;
  buyer_message: string | null;
  assignment_status: string;
  advisor_name: string | null;
  advisor_avatar_url: string | null;
  created_at: string;
}

interface PipelineMessage {
  id: number;
  interest_id: number;
  sender_id: string;
  sender_role: string;
  sender_name: string;
  body: string;
  is_read_by_buyer: boolean;
  is_read_by_advisor: boolean;
  created_at: string;
  edited_at: string | null;
}

function MessageThreadModal({ interestId, title, onClose }: { interestId: number; title: string; onClose: () => void }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<PipelineMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const load = async () => {
    try {
      const data = await api.get<PipelineMessage[]>(`/lead-pipeline/interests/${interestId}/messages`);
      setMessages(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [interestId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const msg = await api.post<PipelineMessage>(`/lead-pipeline/interests/${interestId}/messages`, { body: trimmed });
      setMessages((prev) => [...prev, msg]);
      setBody("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send");
    } finally { setSending(false); }
  };

  const startEdit = (m: PipelineMessage) => { setEditingId(m.id); setEditBody(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };

  const saveEdit = async (m: PipelineMessage) => {
    const next = editBody.trim();
    if (!next || savingEdit) return;
    if (next === m.body) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const updated = await api.patch<PipelineMessage>(`/lead-pipeline/interests/${interestId}/messages/${m.id}`, { body: next });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      cancelEdit();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to edit");
    } finally { setSavingEdit(false); }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, m: PipelineMessage) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  return (
    <ChatSideDrawer
      title={title || "Messages"}
      onClose={onClose}
      subtitle={<><span className="font-semibold text-primary">Niva</span> · AI Deal Assistant · Advisor messages</>}
    >
        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/20">
          {loading ? (
            <div className="flex justify-center py-10"><Loader size="sm" /></div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No messages yet.</p>
              <p className="text-xs text-muted-foreground/60">Your advisor will reach out here once assigned.</p>
            </div>
          ) : (
            messages.map((m) => {
              const isBuyer = m.sender_role === "buyer";
              const mine = user?.id === m.sender_id;
              return (
                <div key={m.id} className={cn("flex flex-col gap-0.5", isBuyer ? "items-end" : "items-start")}>
                  <span className="text-[0.6rem] text-muted-foreground px-1">
                    {m.sender_name} · {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {m.edited_at && <span className="italic"> · edited</span>}
                  </span>
                  {editingId === m.id ? (
                    <div className="w-full max-w-[85%] flex flex-col gap-1.5">
                      <AutoGrowTextarea
                        autoFocus
                        rows={2}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, m)}
                        className="w-full rounded-xl border border-primary/40 bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors">Cancel</button>
                        <Button size="sm" disabled={!editBody.trim() || savingEdit} onClick={() => saveEdit(m)} className="h-7 px-3 text-[11px]">Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("flex items-center gap-1.5", isBuyer ? "flex-row-reverse" : "flex-row")}>
                      <div className={cn(
                        "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed",
                        isBuyer
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-white border border-border text-foreground rounded-tl-sm shadow-sm"
                      )}>
                        {m.body}
                      </div>
                      {mine && (
                        <button
                          onClick={() => startEdit(m)}
                          title="Edit message"
                          aria-label="Edit message"
                          className="shrink-0 h-6 w-6 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Compose */}
        <div className="border-t border-border bg-white px-4 py-3 flex items-end gap-2">
          <AutoGrowTextarea
            rows={2}
            className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
            placeholder="Reply to your advisor…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <Button size="sm" disabled={sending || !body.trim()} onClick={send} className="shrink-0 h-9">
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
    </ChatSideDrawer>
  );
}

export default function BuyerInterestsPage() {
  // Existing coming-soon interests (and their message threads) stay visible
  // when the super admin switches Coming Soon off — only the way back into the
  // deal browser is hidden.
  const comingSoonOn = useModuleVisibility()("coming_soon_deals");
  // Workspace links carry the user's organization prefix.
  const { user } = useAuth();
  // Buyers view this page inside the left sidebar shell, so fill the wider column.
  // Full width for every role — see PageShell in components/interior-page.tsx.
  const shellWidth = "w-full";
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = (tabParam === "coming-soon" || tabParam === "chats" ? tabParam : "active") as
    | "active"
    | "coming-soon"
    | "chats";
  // Which conversation the Chats tab has open, kept in the URL so a
  // notification can deep-link straight into a thread.
  const activeThreadId = searchParams.get("thread");

  const [interests, setInterests] = useState<MyInterest[]>([]);
  const [loading, setLoading] = useState(true);
  // Active-tab page lives in the URL (?page=N, 1-based) so returning from a
  // listing detail via "Back to listings" restores the exact page.
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 1) p.set("page", String(next));
        else p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = usePageSize("interests.pageSize", PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [allStates, setAllStates] = useState<LookupItem[]>([]);

  const [csInterests, setCsInterests] = useState<ComingSoonInterest[]>([]);
  const [csLoading, setCsLoading] = useState(true);
  // Coming-Soon page lives in the URL too (?cspage=N). Its own param, not the
  // Active tab's ?page=, so a mount doesn't have both fetchers writing one key.
  const csPage = Math.max(1, Number(searchParams.get("cspage") ?? "1") || 1);
  const setCsPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 1) p.set("cspage", String(next));
        else p.delete("cspage");
        return p;
      },
      { replace: true },
    );
  };
  const [csTotal, setCsTotal] = useState(0);
  const [csPageSize, setCsPageSize] = usePageSize("interests.csPageSize", PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE);
  const [csViewMode, setCsViewMode] = useState<"grid" | "table">("grid");
  const [msgModal, setMsgModal] = useState<{ interestId: number; title: string } | null>(null);
  // Seeded from the server on mount (so the Chats tab is badged even before
  // it's opened), then kept live by BuyerChats while that tab is showing.
  const [chatUnread, setChatUnread] = useState(0);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    api.get<{ count: number }>("/interests/mine/unread-count")
      .then((d) => setChatUnread(d.count))
      .catch(() => {});
  }, []);

  const openThread = (interestId: string) => setSearchParams({ tab: "chats", thread: interestId });

  const withdraw = async (item: MyInterest) => {
    const title = item.listing_title ?? "this listing";
    if (!confirm(`Withdraw your interest in "${title}"? Your advisor will be notified. You can express interest again later.`)) return;
    setWithdrawingId(item.id);
    try {
      await api.post(`/interests/${item.id}/withdraw`, {});
      toast.success("Interest withdrawn");
      // Step back a page if this emptied the current one.
      const lastOnPage = interests.length === 1 && page > 1;
      fetchInterests(lastOnPage ? page - 1 : page);
      setChatUnread((n) => Math.max(0, n - item.unread_count));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to withdraw interest");
    } finally {
      setWithdrawingId(null);
    }
  };

  // size: passed explicitly when the picker changes, since state hasn't updated yet.
  function fetchCsInterests(targetPage = 1, size = csPageSize) {
    setCsLoading(true);
    api.get<{ total_elements: number; data: ComingSoonInterest[] }>(`/lead-pipeline/mine?page=${targetPage}&page_size=${size}`)
      .then((res) => { setCsInterests(res.data); setCsTotal(res.total_elements); setCsPage(targetPage); })
      .catch(() => {})
      .finally(() => setCsLoading(false));
  }

  useEffect(() => {
    api.get<LookupItem[]>("/lookups/categories").then(setCategories).catch(() => {});
    api.get<LookupItem[]>("/lookups/countries").then(setCountries).catch(() => {});
    api.get<LookupItem[]>("/lookups/states").then(setAllStates).catch(() => {});
    fetchCsInterests(csPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchInterests(targetPage = page, size = pageSize) {
    setLoading(true);
    api.get<{ total_elements: number; data: MyInterest[] }>(`/interests/mine?page=${targetPage}&page_size=${size}`)
      .then((res) => { setInterests(res.data); setTotal(res.total_elements); setPage(targetPage); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchInterests(page); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Passed as router state on each listing link so ListingDetail's "Back to
  // listings" returns to this exact page. Listing links appear only on the
  // active tab, so the page param alone fully restores position.
  const { pathname } = useLocation();
  const backTo = page > 1 ? `${pathname}?page=${page}` : pathname;

  const catName = (id: number | null) => id ? (categories.find((c) => c.id === id)?.name ?? "Business") : "Business";
  const countryNameById = (id: number | null) => id ? (countries.find((c) => c.id === id)?.name ?? null) : null;
  const stateNameById = (id: number | null) => id ? (allStates.find((s) => s.id === id)?.name ?? null) : null;
  const locationLabel = (item: MyInterest) =>
    [item.city_name, item.state_name || stateNameById(item.state_id), item.country_name || countryNameById(item.country_id)]
      .filter(Boolean).join(", ");
  const cfMult = (item: MyInterest) =>
    item.asking_price && item.ebitda && item.ebitda !== 0
      ? (item.asking_price / item.ebitda).toFixed(2) + "X"
      : null;
  const fmtDate = (s: string) => new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  if (loading && activeTab === "active") return <div className="flex flex-1 items-center justify-center py-24"><Loader /></div>;
  if (csLoading && activeTab === "coming-soon") return <div className="flex flex-1 items-center justify-center py-24"><Loader /></div>;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      {/* Shared Dark · Gold interior hero — the same header AI Deal Matching
          and the rest of the interior pages use. */}
      <div className={`${shellWidth} px-4 pt-8 sm:px-6 lg:px-8`}>
        <PageHero
          eyebrow="Your Pipeline"
          icon={Heart}
          title="My"
          titleAccent="Interests"
          description={
            activeTab === "active"
              ? (total === 0 ? "You haven't expressed interest in any listings yet." : `${total} listing${total === 1 ? "" : "s"} you've shown interest in`)
              : activeTab === "chats"
              ? (chatUnread === 0 ? "Your conversations with the BL advisory team." : `${chatUnread} unread message${chatUnread === 1 ? "" : "s"} from your advisors`)
              : (csTotal === 0 ? "No coming-soon interests yet." : `${csTotal} coming-soon deal${csTotal === 1 ? "" : "s"} you've expressed interest in`)
          }
        />
      </div>

      {/* View controls — their own row now that the hero spans the width. */}
      <div className={`${shellWidth} px-4 sm:px-6 lg:px-8`}>
        <div className="pt-6">
          <div className="flex flex-wrap items-center justify-end gap-4">
            {activeTab === "active" && total > 0 && (
              <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
                <button
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    viewMode === "grid"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    viewMode === "table"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>
            )}
            {activeTab === "coming-soon" && csTotal > 0 && (
              <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
                <button
                  onClick={() => setCsViewMode("grid")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    csViewMode === "grid"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => setCsViewMode("table")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    csViewMode === "table"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 rounded-xl border border-border/80 bg-card p-1 w-fit">
            <button
              onClick={() => setSearchParams({})}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                activeTab === "active"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Heart className="h-4 w-4" />
              Active Listings
              {total > 0 && <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-xs">{total}</span>}
            </button>
            <button
              onClick={() => setSearchParams({ tab: "coming-soon" })}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                activeTab === "coming-soon"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Rocket className="h-4 w-4" />
              Coming Soon
              {csTotal > 0 && <span className={cn("ml-1 rounded-full px-1.5 py-0.5 text-xs", activeTab === "coming-soon" ? "bg-white/20" : "bg-primary/10 text-primary")}>{csTotal}</span>}
            </button>
            <button
              onClick={() => setSearchParams({ tab: "chats" })}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                activeTab === "chats"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <MessageSquare className="h-4 w-4" />
              Chats
              {chatUnread > 0 && (
                <span className={cn("ml-1 rounded-full px-1.5 py-0.5 text-xs", activeTab === "chats" ? "bg-white/20" : "bg-primary/10 text-primary")}>{chatUnread}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content — full column width for every view (grids run 4-up on wide screens) */}
      <div className={cn("px-4 sm:px-6 lg:px-8 py-8", shellWidth)}>
        {activeTab === "chats" ? (
          /* ── Chats tab: the buyer's side of listing-interest threads ── */
          <BuyerChats
            activeId={activeThreadId}
            onSelect={(id) =>
              setSearchParams(id ? { tab: "chats", thread: id } : { tab: "chats" })
            }
            onUnreadChange={setChatUnread}
          />
        ) : activeTab === "coming-soon" ? (
          /* ── Coming Soon tab ── */
          csInterests.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-16 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60 text-muted-foreground/60">
                <Rocket className="h-7 w-7" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-foreground">No coming-soon interests yet</h3>
              <p className="mt-2 text-sm text-muted-foreground mb-6">Express interest in coming-soon deals to track them here.</p>
              {comingSoonOn && <Link to={orgScopedPath("/buyer/home?tab=coming-soon", user?.org_slug)}><Button>Browse Coming Soon Deals</Button></Link>}
            </div>
          ) : csLoading ? (
            <div className="flex justify-center py-20"><Loader size="lg" /></div>
          ) : csViewMode === "grid" ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {csInterests.map((item) => (
                <div key={item.interest_id} className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                  {/* Cover image */}
                  <div className="relative h-44 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Building2 className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    {/* Category badges */}
                    {(item.industry_category || item.potential_category) && (
                      <div className="absolute left-3 top-3 flex flex-col gap-1">
                        {item.industry_category && (
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                            {item.industry_category}
                          </span>
                        )}
                        {item.potential_category && (
                          <span className="inline-flex items-center rounded-full border border-violet-200 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-violet-700 backdrop-blur-sm">
                            {item.potential_category}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Coming Soon badge */}
                    <div className="absolute right-3 top-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,hsl(43_74%_60%),hsl(38_64%_46%))] px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[hsl(40_45%_12%)] shadow-sm">
                        <Sparkles className="h-3 w-3" />
                        Coming Soon
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">{item.title}</h3>
                      </div>
                      {item.reference_code && (
                        <p className="mt-0.5 font-mono text-[0.65rem] tracking-wide text-gray-400">{item.reference_code}</p>
                      )}
                      {item.business_description && (
                        <p className="mt-1 line-clamp-3 text-xs leading-5 text-gray-500">{item.business_description}</p>
                      )}
                    </div>

                    {/* Status badge */}
                    <div>
                      <span className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
                        item.assignment_status === "accepted" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500" :
                        item.assignment_status === "rejected" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" :
                        "border-amber-500/30 bg-amber-500/10 text-amber-500"
                      )}>
                        <ShieldCheck className="h-3 w-3" />
                        {item.assignment_status === "accepted" ? "Advisor assigned" :
                         item.assignment_status === "rejected" ? "Reassigning advisor" :
                         "Awaiting advisor"}
                      </span>
                    </div>

                    {/* Location */}
                    {item.location && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span>{item.location}</span>
                      </div>
                    )}

                    {/* Advisor */}
                    {item.advisor_name && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        {item.advisor_avatar_url ? (
                          <img src={assetUrl(item.advisor_avatar_url)!} alt={item.advisor_name} className="h-7 w-7 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Users className="h-3.5 w-3.5" />
                          </div>
                        )}
                        <span className="text-xs text-gray-500">
                          BL Advisor: <span className="font-semibold text-gray-800">{item.advisor_name}</span>
                        </span>
                      </div>
                    )}

                    {/* Buyer message */}
                    {item.buyer_message && (
                      <p className="text-[0.7rem] italic text-muted-foreground line-clamp-2">"{item.buyer_message}"</p>
                    )}

                    <div className="mt-auto pt-1 flex flex-col gap-2">
                      <button
                        onClick={() => setMsgModal({ interestId: item.interest_id, title: item.title })}
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4" />
                        Messages
                      </button>
                      {comingSoonOn && (
                      <Link to={orgScopedPath("/buyer/home?tab=coming-soon", user?.org_slug)} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-800 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                        View Coming Soon Deals
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Coming Soon table view */
            <div className="overflow-x-auto rounded-2xl border border-border/80 bg-card">
              <table className="min-w-full divide-y divide-border/60">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">Deal</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Advisor</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {csInterests.map((item) => (
                    <tr key={item.interest_id} className="transition-colors hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                            {item.image_url
                              ? <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                              : <div className="flex h-full w-full items-center justify-center"><Building2 className="h-5 w-5 text-slate-300" /></div>}
                          </div>
                          <span className="line-clamp-2 text-sm font-semibold text-foreground">{item.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {item.industry_category && <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">{item.industry_category}</span>}
                          {item.potential_category && <span className="inline-flex rounded-full border border-violet-500/30 bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-500">{item.potential_category}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{item.location ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
                          item.assignment_status === "accepted" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500" :
                          item.assignment_status === "rejected" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" :
                          "border-amber-500/30 bg-amber-500/10 text-amber-500"
                        )}>
                          {item.assignment_status === "accepted" ? "Assigned" :
                           item.assignment_status === "rejected" ? "Reassigning" : "Awaiting"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{item.advisor_name ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 whitespace-nowrap"
                            onClick={() => setMsgModal({ interestId: item.interest_id, title: item.title })}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            Messages
                          </Button>
                          <Link to={orgScopedPath("/buyer/home?tab=coming-soon", user?.org_slug)}>
                            <Button size="sm" variant="outline" className="border-primary/15 bg-white/80 hover:bg-white whitespace-nowrap">
                              View
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) :
          total === 0 && !loading ? (
          <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60 text-muted-foreground/60">
              <Heart className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-foreground">No interests yet</h3>
            <p className="mt-2 text-sm text-muted-foreground mb-6">Browse listings and click "Show Interest" to track them here.</p>
            <Link to={orgScopedPath("/buyer/home", user?.org_slug)}><Button>Browse Listings</Button></Link>
          </div>
        ) : viewMode === "grid" ? (
          /* ── Grid view ── */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {interests.map((item) => {
              const loc = locationLabel(item);
              const cf = cfMult(item);
              const imgSrc = item.listing_image_url ? assetUrl(item.listing_image_url) : null;
              const description = item.display_overview;
              const status = dealStatusChip(item);
              return (
                <div key={item.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">

                  {/* Cover image */}
                  <div className="relative h-44 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    {imgSrc ? (
                      <img src={imgSrc} alt={item.listing_title ?? ""} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Building2 className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    {/* Category badge */}
                    <div className="absolute left-3 top-3">
                      <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                        {catName(item.listing_category_id)}
                      </span>
                    </div>
                    {status && (
                      <div className="absolute right-3 top-3">
                        <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.1em] backdrop-blur-sm", status.className)}>
                          {status.label}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                          {item.listing_title ?? catName(item.listing_category_id)}
                        </h3>
                      </div>
                      {item.listing_reference_code && (
                        <p className="mt-0.5 font-mono text-[0.65rem] tracking-wide text-gray-400">{item.listing_reference_code}</p>
                      )}
                      {description && (
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{description}</p>
                      )}
                    </div>

                    {/* Financial metrics — identical tiles to the Home marketplace cards */}
                    <div className="grid grid-cols-2 gap-2">
                      {item.asking_price && (
                        <div className="rounded-xl border border-border/50 bg-primary/10 px-3 py-2.5">
                          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Asking</div>
                          <div className="mt-0.5 text-sm font-extrabold text-primary">{fmtCompact(item.asking_price)}</div>
                        </div>
                      )}
                      {item.revenue && (
                        <div className="rounded-xl border border-border/50 bg-primary/10 px-3 py-2.5">
                          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">Revenue</div>
                          <div className="mt-0.5 text-sm font-extrabold text-foreground">{fmtCompact(item.revenue)}</div>
                        </div>
                      )}
                      {item.ebitda && (
                        <div className="rounded-xl border border-border/50 bg-primary/10 px-3 py-2.5">
                          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">EBITDA</div>
                          <div className="mt-0.5 text-sm font-extrabold text-foreground">{fmtCompact(item.ebitda)}</div>
                        </div>
                      )}
                      {cf && (
                        <div className="rounded-xl border border-border/50 bg-primary/10 px-3 py-2.5">
                          <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">CF Mult</div>
                          <div className="mt-0.5 text-sm font-extrabold text-foreground">{cf}</div>
                        </div>
                      )}
                    </div>

                    {/* Location */}
                    {loc && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span>{loc}</span>
                      </div>
                    )}

                    {/* BL Advisor */}
                    {item.agent_name && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        {item.agent_avatar_url ? (
                          <img src={assetUrl(item.agent_avatar_url)!} alt={item.agent_name} className="h-7 w-7 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Users className="h-3.5 w-3.5" />
                          </div>
                        )}
                        <span className="text-xs text-gray-500">
                          BL Advisor: <span className="font-semibold text-gray-800">{item.agent_name}</span>
                        </span>
                      </div>
                    )}

                    {/* The buyer's own interest note */}
                    {item.message && (
                      <p className="line-clamp-2 text-[0.7rem] text-muted-foreground">"{item.message}"</p>
                    )}

                    {/* Actions */}
                    <div className="mt-auto flex flex-col gap-2 pt-1">
                      {item.listing_removed ? (
                        <span className="flex w-full items-center justify-center rounded-xl border border-border/80 py-2.5 text-sm font-semibold text-muted-foreground">
                          Listing removed
                        </span>
                      ) : (
                        <Link to={`/listings/${item.listing_id}`}
                          state={{ from: backTo }}
                          className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06]">
                          View listing
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => openThread(item.id)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          Messages
                          {item.unread_count > 0 && (
                            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">
                              {item.unread_count}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => withdraw(item)}
                          disabled={withdrawingId === item.id}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border/80 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-rose-500/40 hover:text-rose-500 disabled:opacity-50"
                        >
                          <Undo2 className="h-3.5 w-3.5" />
                          {withdrawingId === item.id ? "Withdrawing…" : "Withdraw"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Table view ── */
          <div className="rounded-2xl border border-border/80 bg-card shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Asking Price</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">EBITDA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">C.F. Multiple</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">BL Advisor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Date</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {interests.map((item) => {
                    const loc = locationLabel(item);
                    const cf = cfMult(item);
                    const status = dealStatusChip(item);
                    return (
                      <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center rounded-full border border-primary/15 bg-primary/10 px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.1em] text-primary whitespace-nowrap">
                            {catName(item.listing_category_id)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 max-w-[200px]">
                          <span className="font-semibold text-foreground line-clamp-1">
                            {item.listing_title ?? catName(item.listing_category_id)}
                          </span>
                          {item.message && (
                            <p className="text-[0.68rem] text-muted-foreground truncate">"{item.message}"</p>
                          )}
                          {status && (
                            <span className={cn("mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.1em]", status.className)}>
                              {status.label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-emerald-500">{fmtCompact(item.asking_price)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-sky-500">{fmtCompact(item.revenue)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-violet-500">{fmtCompact(item.ebitda)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {cf
                            ? <span className="font-bold text-amber-500">{cf}</span>
                            : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {loc ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {loc}
                            </span>
                          ) : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          {item.agent_name ? (
                            <div className="flex items-center gap-1.5">
                              {item.agent_avatar_url ? (
                                <img src={assetUrl(item.agent_avatar_url)!} alt={item.agent_name} className="h-6 w-6 rounded-full object-cover" />
                              ) : (
                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                                  <Users className="h-3 w-3" />
                                </div>
                              )}
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{item.agent_name}</span>
                            </div>
                          ) : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-xs text-muted-foreground">
                          {fmtDate(item.created_at)}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 whitespace-nowrap"
                              onClick={() => openThread(item.id)}
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                              Messages
                              {item.unread_count > 0 && (
                                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">
                                  {item.unread_count}
                                </span>
                              )}
                            </Button>
                            {!item.listing_removed && (
                              <Link to={`/listings/${item.listing_id}`} state={{ from: backTo }}>
                                <Button size="sm" variant="outline" className="border-primary/15 bg-white/80 hover:bg-white whitespace-nowrap">
                                  View
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              title="Withdraw interest"
                              aria-label="Withdraw interest"
                              disabled={withdrawingId === item.id}
                              onClick={() => withdraw(item)}
                              className="whitespace-nowrap text-muted-foreground hover:text-rose-500"
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "active" && (
          <Pagination
            page={page}
            totalElements={total}
            pageSize={pageSize}
            onChange={(p) => fetchInterests(p)}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(size) => { setPageSize(size); fetchInterests(1, size); }}
            className="mt-6"
          />
        )}
        {activeTab === "coming-soon" && (
          <Pagination
            page={csPage}
            totalElements={csTotal}
            pageSize={csPageSize}
            onChange={(p) => fetchCsInterests(p)}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(size) => { setCsPageSize(size); fetchCsInterests(1, size); }}
            className="mt-6"
          />
        )}
      </div>

      {/* Message thread modal */}
      {msgModal && (
        <MessageThreadModal
          interestId={msgModal.interestId}
          title={msgModal.title}
          onClose={() => setMsgModal(null)}
        />
      )}
    </div>
  );
}
